import type { ForwardedRef, MutableRefObject } from "react";
import { useEffect, useRef, useState } from "react";

import load from "load-script";

import { useConfig } from "@jti/app-context/useConfig";
import { useCheckoutConfiguration } from "@jti/commerce/providers";
import { newrelic } from "@jti/fe-clients";
import { createPgsClientConfig } from "@jti/fe-common";
import { useDispatch, useSelector } from "@jti/store/reduxHooks";
import { selectOrderNumber } from "@jti/store/selectors/selectOrderNumber";
import { setIsPgsClientPending } from "@jti/store/slices/LEGACY__checkoutSlice";
import { useTranslations } from "@jti/ui";

import { useFailedPaymentFlow } from "@checkout/hooks";
import { useFreeTrial } from "@checkout/hooks/useFreeTrial";

import { getPgsCallbackUrls } from "./getPgsCallbackUrls";

export type UsePgsClient = {
  ref: ForwardedRef<HTMLDivElement | null>;
  paymentCode: string;
  onUnmount: () => void;
  buttonClassName?: string;
  orderEntityId?: string;
  orderId?: string;
};

export default function usePgsClient({ ref, paymentCode, onUnmount, buttonClassName, orderId }: UsePgsClient): void {
  const dispatch = useDispatch();
  const { __ } = useTranslations();
  const config = useConfig();
  const prevPaymentCode = useRef<string | null>(null);
  const { isFailedPaymentFlow } = useFailedPaymentFlow();
  const { failedPaymentFlow } = useCheckoutConfiguration();
  const { isFreeTrial } = useFreeTrial();
  const orderEntityId = useSelector(selectOrderNumber(isFreeTrial));

  const paymentToken = useSelector((state) => state.checkout.paymentToken);
  const pgsAdapter = useSelector((state) => state.checkout.pgsAdapter);

  const [loadedClient, setLoadedClient] = useState<string | null>(null);

  // Ref so other effects can unmount the widget too, not just this one's cleanup.
  const mountedClientRef = useRef<{ code: string; unmount: () => void } | null>(null);
  const retryPaymentButtonLabel = failedPaymentFlow?.retryPaymentButtonLabel || __("cart.order.payment.pay");

  useEffect(() => {
    if (prevPaymentCode.current !== paymentCode) {
      prevPaymentCode.current = paymentCode;
    }
  }, [paymentCode]);

  // Unmount the card widget if it no longer matches the selected payment method.
  useEffect(() => {
    if (mountedClientRef.current && mountedClientRef.current.code !== paymentCode) {
      mountedClientRef.current.unmount();
      mountedClientRef.current = null;
    }
  }, [paymentCode]);

  useEffect(() => {
    if (!orderEntityId || !pgsAdapter?.client?.length || !orderId || !paymentToken) {
      return;
    }

    const { paymentSuccessfulUrl, paymentFailedUrl } = getPgsCallbackUrls({
      orderId,
      paymentToken,
      isFreeTrial,
    });

    const onClientLoad = () => {
      dispatch(setIsPgsClientPending(false));

      const PGS_CLIENT = window.__PGSClient;

      if (PGS_CLIENT) {
        const client = new PGS_CLIENT(
          createPgsClientConfig(config, "pgsUrl", {
            paymentToken,
            paymentMethod: pgsAdapter.paymentCode,
            successUrl: paymentSuccessfulUrl,
            cancelUrl: paymentFailedUrl,
            buttonName: isFailedPaymentFlow ? retryPaymentButtonLabel : __("cart.order.payment.pay"),
            buttonClassName,
            merchant: config.paymentMerchantName,
          })
        );

        const reference = ref as MutableRefObject<null>;
        if (reference?.current) {
          const unmount = client.mount(reference.current);
          mountedClientRef.current = { code: pgsAdapter.paymentCode, unmount };
        }
      }
    };

    if (pgsAdapter.client !== loadedClient) {
      load(pgsAdapter.client, (error) => {
        if (error === null) {
          setLoadedClient(pgsAdapter.client);
          onClientLoad();

          return;
        }

        // TODO: handle PGS adapter script loading error
        console.error("Failed to load PGS ADAPTER SCRIPT!", error);
        newrelic.noticeError(new Error(`Failed to load PGS ADAPTER SCRIPT! ${error}`));
        dispatch(setIsPgsClientPending(false)); // Fix infinite spinner issue on status 400
      });
    } else {
      onClientLoad();
    }

    return () => {
      if (mountedClientRef.current?.code === pgsAdapter.paymentCode) {
        mountedClientRef.current.unmount();
        mountedClientRef.current = null;
      }
      onUnmount();
    };
  }, [
    pgsAdapter,
    paymentToken,
    ref,
    onUnmount,
    buttonClassName,
    orderEntityId,
    prevPaymentCode,
    retryPaymentButtonLabel,
  ]);
}
